import React, { useEffect, useState } from "react"

import MapWebView from "@fuse/sharedComponents/map"
import { AnimatePresence, motion } from "framer-motion"
import {
  atom,
  RecoilRoot,
  selector,
  useRecoilState,
  useRecoilValue,
} from "recoil"

// Replace with your actual map import

/* ----------------- RECOIL ----------------- */

type UserSlot = "creator" | "friend1" | "friend2"

interface UserData {
  joined?: boolean
  verified?: boolean
  phone?: string
  name?: string
  isLucky?: boolean // if this user sees the full activity
  // Preferences
  budget?: string
  timePref?: string
  distance?: string
  location?: string
  interests?: string[]
}

const usersAtom = atom<Record<UserSlot, UserData>>({
  key: "usersAtom",
  default: { creator: {}, friend1: {}, friend2: {} },
})

const currentUserAtom = atom<UserSlot | null>({
  key: "currentUserAtom",
  default: null,
})

/** The final spin data. We store partial info + the full activity. */
interface SpinResult {
  activity: string | null // e.g. "Bowling 🎳"
  time: string | null // partial info for all
  place: string | null // partial info for all
}
const spinAtom = atom<SpinResult>({
  key: "spinAtom",
  default: { activity: null, time: null, place: null },
})

/** Toast system */
interface ToastMsg {
  id: number
  text: string
}
const toastsAtom = atom<ToastMsg[]>({
  key: "toastsAtom",
  default: [],
})
let toastCounter = 1
function useToast() {
  const [toasts, setToasts] = useRecoilState(toastsAtom)
  function showToast(msg: string) {
    const newT = { id: toastCounter++, text: msg }
    setToasts((prev) => [...prev, newT])
    setTimeout(() => {
      setToasts((cur) => cur.filter((x) => x.id !== newT.id))
    }, 3000)
  }
  return showToast
}

/** We only show user in table if joined===true. */
const joinedUsersSelector = selector<[UserSlot, UserData][]>({
  key: "joinedUsersSelector",
  get: ({ get }) => {
    const us = get(usersAtom)
    return (Object.entries(us) as [UserSlot, UserData][]).filter(
      ([slot, data]) => data.joined,
    )
  },
})

/* Some final activities to pick from. */
const possibleActivities = [
  "Bowling 🎳",
  "Karaoke 🎤",
  "Escape Room 🔐",
  "Board Games ♟",
]

/* ------------------ COMPONENTS ------------------ */

/** Toast container top-right. */
function ToastContainer() {
  const toasts = useRecoilValue(toastsAtom)
  return (
    <div
      style={{
        position: "fixed",
        top: 10,
        right: 10,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
      }}
    >
      <AnimatePresence>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            style={{
              background: "#444",
              color: "#fff",
              padding: "0.8rem 1rem",
              borderRadius: 6,
              boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
              maxWidth: 220,
              fontSize: "0.9rem",
            }}
          >
            {t.text}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}

/** A phone verify modal with black text on white input. */
function PhoneModal({
  onClose,
  onVerify,
}: {
  onClose: () => void
  onVerify: (phone: string, name?: string) => void
}) {
  const showToast = useToast()
  const [phone, setPhone] = useState("")
  const [codeSent, setCodeSent] = useState(false)
  const [digits, setDigits] = useState(["", "", "", ""])
  const [hasAcct, setHasAcct] = useState<boolean | undefined>()
  const [name, setName] = useState("")

  function handlePhoneChange(val: string) {
    const cleaned = val.replace(/[^\d+]/g, "")
    setPhone(cleaned)
  }
  function handleSendCode() {
    if (!phone || phone.length < 5) {
      showToast("Invalid phone")
      return
    }
    setCodeSent(true)
    showToast(`Sending code to ${phone}`)
  }
  function handleDigitChange(i: number, val: string) {
    if (!/^\d?$/.test(val)) return
    const nd = [...digits]
    nd[i] = val
    setDigits(nd)
    if (val && i < 3) {
      const nxt = document.getElementById(`vfCode-${i + 1}`)
      if (nxt) (nxt as HTMLInputElement).focus()
    } else if (!val && i > 0) {
      const prv = document.getElementById(`vfCode-${i - 1}`)
      if (prv) (prv as HTMLInputElement).focus()
    }
  }
  function handleVerifyCode() {
    const cval = digits.join("")
    if (cval.length < 4) {
      showToast("Enter code fully")
      return
    }
    showToast("Code verified!")
    // phone ends in '0' => existing
    if (phone.endsWith("0")) setHasAcct(true)
    else setHasAcct(false)
  }
  function handleFinish() {
    if (hasAcct) {
      onVerify(phone)
      showToast("Welcome back!")
      onClose()
    } else {
      if (!name.trim()) {
        showToast("Enter your name")
        return
      }
      onVerify(phone, name.trim())
      showToast(`Welcome, ${name.trim()}!`)
      onClose()
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.6)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#222",
          color: "#fff",
          padding: "1.5rem",
          borderRadius: 8,
          width: 320,
          position: "relative",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            background: "transparent",
            color: "#ccc",
            border: "none",
            fontSize: "1.2rem",
            cursor: "pointer",
          }}
        >
          ✕
        </button>
        <h2 style={{ marginTop: 0, marginBottom: "0.5rem" }}>Log In</h2>
        <p style={{ fontSize: "0.9rem", marginBottom: "1rem" }}>
          Continue with your phone number. No spam, promise!
        </p>
        {!codeSent && hasAcct === undefined && (
          <>
            <label style={{ fontSize: "0.85rem" }}>Phone:</label>
            <input
              type="text"
              value={phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              style={{
                width: "100%",
                marginBottom: "0.5rem",
                padding: "0.4rem",
                color: "#000",
              }}
            />
            <button
              onClick={handleSendCode}
              style={{
                background: "#7F5AF0",
                color: "#fff",
                border: "none",
                padding: "0.5rem 1rem",
                borderRadius: 6,
              }}
            >
              Send Code
            </button>
          </>
        )}
        {codeSent && hasAcct === undefined && (
          <>
            <div
              style={{ display: "flex", gap: "0.3rem", marginBottom: "0.5rem" }}
            >
              {digits.map((d, i) => (
                <input
                  key={i}
                  id={`vfCode-${i}`}
                  value={d}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  style={{
                    width: 40,
                    height: 40,
                    textAlign: "center",
                    fontSize: "1.2rem",
                    border: "1px solid #555",
                    borderRadius: 4,
                    color: "#000",
                  }}
                />
              ))}
            </div>
            <button
              onClick={handleVerifyCode}
              style={{
                background: "#7F5AF0",
                color: "#fff",
                border: "none",
                padding: "0.5rem 1rem",
                borderRadius: 6,
              }}
            >
              Verify Code
            </button>
          </>
        )}
        {hasAcct !== undefined && (
          <>
            {hasAcct ? (
              <p>We recognize your account from phone {phone}!</p>
            ) : (
              <>
                <p>You're new. Enter your name:</p>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    width: "100%",
                    marginBottom: "0.5rem",
                    padding: "0.4rem",
                    color: "#000",
                  }}
                />
              </>
            )}
            <button
              onClick={handleFinish}
              style={{
                background: "#7F5AF0",
                color: "#fff",
                border: "none",
                padding: "0.5rem 1rem",
                borderRadius: 6,
              }}
            >
              Continue
            </button>
          </>
        )}
        <div style={{ fontSize: "0.8rem", color: "#aaa", marginTop: "1rem" }}>
          By entering your number, you accept our{" "}
          <a href="#" style={{ color: "#7F5AF0" }}>
            Terms
          </a>{" "}
          &{" "}
          <a href="#" style={{ color: "#7F5AF0" }}>
            Privacy
          </a>
          .
        </div>
      </div>
    </div>
  )
}

/** The share sheet w/ partial qr code placeholder. */
function ShareSheet({ onClose }: { onClose: () => void }) {
  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        background: "#333",
        color: "#fff",
        borderRadius: "16px 16px 0 0",
        padding: "1rem",
        zIndex: 9999,
      }}
    >
      <div style={{ textAlign: "right" }}>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            color: "#ccc",
            border: "none",
            fontSize: "1.2rem",
          }}
        >
          ✕
        </button>
      </div>
      <h2 style={{ marginTop: 0, marginBottom: "0.5rem" }}>
        Share with a Friend
      </h2>
      <p style={{ fontSize: "0.9rem" }}>
        Send them this link or let them scan the QR code:
      </p>
      <div
        style={{
          width: 120,
          height: 120,
          background: "#666",
          margin: "1rem auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 8,
        }}
      >
        QR
      </div>
      <p
        style={{
          background: "#222",
          padding: "0.5rem",
          borderRadius: 6,
          textAlign: "center",
        }}
      >
        https://myapp.com/invite/abc123
      </p>
    </div>
  )
}

/** The ActivityPreferences panel. We'll do unconditional hooks, partial returns, etc. */
function ActivityPreferences() {
  const [open, setOpen] = useState(false)
  const [users, setUsers] = useRecoilState(usersAtom)
  const current = useRecoilValue(currentUserAtom)
  const showToast = useToast()

  // local
  const [budget, setBudget] = useState("Under $25")
  const [timePref, setTimePref] = useState("Tonight")
  const [distance, setDistance] = useState("5 miles")
  const [location, setLocation] = useState("")
  const [interests, setInterests] = useState<string[]>([])

  const me = current ? users[current] : undefined

  useEffect(() => {
    if (me?.joined) {
      setBudget(me.budget || "Under $25")
      setTimePref(me.timePref || "Tonight")
      setDistance(me.distance || "5 miles")
      setLocation(me.location || "")
      setInterests(me.interests || [])
    }
  }, [
    me?.joined,
    me?.budget,
    me?.timePref,
    me?.distance,
    me?.location,
    me?.interests,
  ])

  if (!me || !me.joined) return null

  const budgets = ["Under $25", "Under $50", "Under $100", "No Limit"]
  const times = ["Tonight", "Tomorrow", "This Weekend"]
  const dists = ["5 miles", "10 miles", "25 miles", "50 miles"]
  const interOptions = [
    "Hiking",
    "Board Games",
    "Movies",
    "Karaoke",
    "Dining",
    "Outdoors",
  ]

  function toggleInterest(i: string) {
    if (interests.includes(i)) setInterests(interests.filter((x) => x !== i))
    else setInterests([...interests, i])
  }
  function handleSave() {
    if (!me.verified) {
      showToast("Must log in first!")
      return
    }
    setUsers({
      ...users,
      [current as string]: {
        ...me,
        budget,
        timePref,
        distance,
        location,
        interests,
      },
    })
    showToast("Preferences saved!")
  }

  return (
    <div style={{ background: "#333", borderRadius: 8, marginBottom: "1rem" }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          padding: "0.8rem 1rem",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3 style={{ margin: 0, fontSize: "1rem" }}>Activity Preferences</h3>
        <motion.div
          animate={{ rotate: open ? 90 : 0 }}
          transition={{ duration: 0.2 }}
          style={{ fontSize: "1.2rem", color: "#ccc" }}
        >
          ▶
        </motion.div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ overflow: "hidden", padding: "0 1rem 1rem" }}
          >
            <div style={{ marginTop: "1rem" }}>
              <p style={{ fontSize: "0.9rem", marginBottom: "0.3rem" }}>
                Budget:
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                  marginBottom: "0.8rem",
                }}
              >
                {budgets.map((b) => (
                  <button
                    key={b}
                    onClick={() => setBudget(b)}
                    style={{
                      background: budget === b ? "#fff" : "#444",
                      color: budget === b ? "#333" : "#fff",
                      borderRadius: 20,
                      border: "none",
                      padding: "0.4rem 0.8rem",
                      cursor: "pointer",
                    }}
                  >
                    {b}
                  </button>
                ))}
              </div>

              <p style={{ fontSize: "0.9rem", marginBottom: "0.3rem" }}>
                Time Pref:
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                  marginBottom: "0.8rem",
                }}
              >
                {times.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTimePref(t)}
                    style={{
                      background: timePref === t ? "#fff" : "#444",
                      color: timePref === t ? "#333" : "#fff",
                      borderRadius: 20,
                      border: "none",
                      padding: "0.4rem 0.8rem",
                      cursor: "pointer",
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <p style={{ fontSize: "0.9rem", marginBottom: "0.3rem" }}>
                Distance:
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                  marginBottom: "0.8rem",
                }}
              >
                {dists.map((d) => (
                  <button
                    key={d}
                    onClick={() => setDistance(d)}
                    style={{
                      background: distance === d ? "#fff" : "#444",
                      color: distance === d ? "#333" : "#fff",
                      borderRadius: 20,
                      border: "none",
                      padding: "0.4rem 0.8rem",
                      cursor: "pointer",
                    }}
                  >
                    {d}
                  </button>
                ))}
              </div>

              <p style={{ fontSize: "0.9rem", marginBottom: "0.3rem" }}>
                Location:
              </p>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                style={{
                  width: "100%",
                  marginBottom: "0.5rem",
                  padding: "0.4rem",
                  color: "#000",
                }}
              />
              <MapWebView coordinate={{ lat: 40.7128, lng: -74.006 }} />

              <p
                style={{
                  fontSize: "0.9rem",
                  marginBottom: "0.3rem",
                  marginTop: "0.8rem",
                }}
              >
                Interests:
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                  marginBottom: "0.8rem",
                }}
              >
                {interOptions.map((opt) => {
                  const sel = interests.includes(opt)
                  return (
                    <button
                      key={opt}
                      onClick={() => toggleInterest(opt)}
                      style={{
                        background: sel ? "#fff" : "#444",
                        color: sel ? "#333" : "#fff",
                        borderRadius: 20,
                        border: "none",
                        padding: "0.4rem 0.8rem",
                        cursor: "pointer",
                      }}
                    >
                      {opt}
                    </button>
                  )
                })}
              </div>

              <div style={{ textAlign: "right" }}>
                <button
                  onClick={handleSave}
                  style={{
                    background: "#7F5AF0",
                    color: "#fff",
                    border: "none",
                    borderRadius: 20,
                    padding: "0.5rem 1rem",
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/** The partial reveal wheel. We'll store partial info in spinAtom: time/place. The lucky user sees finalActivity. Others see the partial. */
function MysteryWheel() {
  const [angle, setAngle] = useState(0)
  const [spinning, setSpinning] = useState(false)
  const [spinState, setSpinState] = useRecoilState(spinAtom)
  const [users, setUsers] = useRecoilState(usersAtom)
  const joined = useRecoilValue(joinedUsersSelector)
  const partialTime = spinState.time
  const partialPlace = spinState.place
  const fullActivity = spinState.activity

  // We'll do the actual spin logic here or in CTA.
  // Let's do it here for demonstration:
  function doSpin() {
    if (spinning) return
    setSpinning(true)
    const newAngle = 720 + Math.floor(Math.random() * 360)
    setAngle(newAngle)
    setTimeout(() => {
      setSpinning(false)
      // pick random activity from possible
      const chosen =
        possibleActivities[
          Math.floor(Math.random() * possibleActivities.length)
        ]
      // also pick random time & place from some small list
      const randomTime = ["8pm", "Tomorrow 6pm", "Sat 3pm"][
        Math.floor(Math.random() * 3)
      ]
      const randomPlace = ["Downtown Lanes", "City Karaoke", "Escape Center"][
        Math.floor(Math.random() * 3)
      ]
      // store that in spinAtom
      setSpinState({
        activity: chosen,
        time: randomTime,
        place: randomPlace,
      })
      // pick a random user as isLucky
      if (joined.length > 0) {
        const r = Math.floor(Math.random() * joined.length)
        const luckySlot = joined[r][0]
        const newObj = { ...users }
        Object.keys(newObj).forEach((key) => {
          const k = key as UserSlot
          newObj[k] = { ...newObj[k], isLucky: false }
        })
        newObj[luckySlot] = { ...newObj[luckySlot], isLucky: true }
        setUsers(newObj)
      }
    }, 3500)
  }

  let nextText = "Need 2+ friends to spin."
  if (joined.length >= 2 && !fullActivity) nextText = "Tap the wheel to spin!"
  if (fullActivity)
    nextText =
      "One friend sees the full activity, others see partial (time/place)."

  return (
    <div
      style={{
        background: "#333",
        borderRadius: 8,
        padding: "1rem",
        marginBottom: "1rem",
      }}
    >
      <h3 style={{ margin: 0, fontSize: "1rem", marginBottom: "0.5rem" }}>
        Mystery Wheel
      </h3>
      <p style={{ fontSize: "0.9rem", marginBottom: "1rem" }}>{nextText}</p>
      {fullActivity && (
        <p style={{ fontSize: "0.9rem", marginBottom: "0.8rem" }}>
          Activity: <strong>{fullActivity}</strong>
          <br />
          Time: <strong>{partialTime}</strong>
          <br />
          Place: <strong>{partialPlace}</strong>
        </p>
      )}
      {!fullActivity && partialTime && partialPlace && (
        <p style={{ fontSize: "0.9rem", marginBottom: "0.8rem" }}>
          Time: {partialTime}, Place: {partialPlace} (activity unknown if not
          lucky)
        </p>
      )}
      <div style={{ textAlign: "center", marginBottom: "1rem" }}>
        <motion.div
          style={{
            width: 140,
            height: 140,
            borderRadius: "50%",
            margin: "0 auto",
            background: "conic-gradient(#7F5AF0,#444,#7F5AF0)",
          }}
          animate={{ rotate: angle }}
          transition={{ duration: 3.5, ease: "easeInOut" }}
          onClick={() => {
            if (joined.length < 2) return // ignore if not enough joined
            doSpin()
          }}
        />
      </div>
      {fullActivity && (
        <button
          onClick={() => {
            // spin again
            setSpinState({ activity: null, time: null, place: null })
            // reset isLucky
            const newObj = { ...users }
            Object.keys(newObj).forEach((k) => {
              const slot = k as UserSlot
              newObj[slot] = { ...newObj[slot], isLucky: false }
            })
            setUsers(newObj)
          }}
          style={{
            background: "#7F5AF0",
            color: "#fff",
            border: "none",
            borderRadius: 20,
            padding: "0.5rem 1rem",
            cursor: "pointer",
          }}
        >
          Respin for a new activity
        </button>
      )}
    </div>
  )
}

/** A table of joined users. Lucky user sees full, others see partial. We'll rely on MysteryWheel to show partial info. 
    Here we just show the "isLucky" column. */
function JoinedUsersTable() {
  const joined = useRecoilValue(joinedUsersSelector)
  if (joined.length === 0) return null

  return (
    <div
      style={{
        background: "#222",
        borderRadius: 8,
        padding: "1rem",
        marginBottom: "1rem",
      }}
    >
      <h3 style={{ marginTop: 0, fontSize: "1rem", marginBottom: "0.5rem" }}>
        Joined Users
      </h3>
      <table
        style={{
          width: "100%",
          color: "#fff",
          fontSize: "0.9rem",
          borderCollapse: "collapse",
        }}
      >
        <thead>
          <tr style={{ borderBottom: "1px solid #333" }}>
            <th style={{ textAlign: "left", paddingBottom: "0.3rem" }}>User</th>
            <th style={{ textAlign: "left", paddingBottom: "0.3rem" }}>
              Budget
            </th>
            <th style={{ textAlign: "left", paddingBottom: "0.3rem" }}>
              TimePref
            </th>
            <th style={{ textAlign: "left", paddingBottom: "0.3rem" }}>
              Lucky?
            </th>
          </tr>
        </thead>
        <tbody>
          {joined.map(([slot, u]) => {
            return (
              <tr key={slot} style={{ borderBottom: "1px solid #333" }}>
                <td style={{ padding: "0.4rem 0" }}>
                  {slot.toUpperCase()} {u.name ? `(${u.name})` : ""}
                </td>
                <td style={{ padding: "0.4rem 0" }}>{u.budget || "(none)"}</td>
                <td style={{ padding: "0.4rem 0" }}>
                  {u.timePref || "(none)"}
                </td>
                <td style={{ padding: "0.4rem 0" }}>
                  {u.isLucky ? "Yes" : "No"}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** Main UI. */
function MainUI() {
  const [users, setUsers] = useRecoilState(usersAtom)
  const [current, setCurrent] = useRecoilState(currentUserAtom)

  function handleSelect(slot: UserSlot) {
    setUsers({
      ...users,
      [slot]: { ...users[slot], joined: true },
    })
    setCurrent(slot)
  }
  function handleResetAll() {
    setUsers({ creator: {}, friend1: {}, friend2: {} })
  }
  function handleHome() {
    // setUsers({ creator: {}, friend1: {}, friend2: {} })
    setCurrent(null)
  }

  const me = current ? users[current] : undefined

  return (
    <div style={{ padding: "1rem", maxWidth: 800, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <button
          onClick={() => handleResetAll()}
          style={{
            background: "#555",
            color: "#fff",
            border: "none",
            padding: "0.5rem 1rem",
            borderRadius: 6,
          }}
        >
          Reset All
        </button>
        <button
          onClick={() => handleHome()}
          style={{
            background: "#555",
            color: "#fff",
            border: "none",
            padding: "0.5rem 1rem",
            borderRadius: 6,
          }}
        >
          home
        </button>
        <div style={{ fontSize: "0.8rem", color: "#ccc" }}>
          <a href="#" style={{ color: "#7F5AF0", marginRight: "1rem" }}>
            Terms
          </a>
          <a href="#" style={{ color: "#7F5AF0" }}>
            Privacy
          </a>
        </div>
      </div>

      {/* If no user selected, show the selection */}
      {!current && (
        <div style={{ marginBottom: "2rem" }}>
          <h2 style={{ margin: "0 0 0.5rem" }}>Select a user to join:</h2>
          <button
            onClick={() => handleSelect("creator")}
            style={{ marginRight: "0.5rem" }}
          >
            Creator
          </button>
          <button
            onClick={() => handleSelect("friend1")}
            style={{ marginRight: "0.5rem" }}
          >
            Friend 1
          </button>
          <button onClick={() => handleSelect("friend2")}>Friend 2</button>
        </div>
      )}

      {/* If user is selected, show partial info */}
      {current && (
        <div>
          {me?.verified ? (
            <p style={{ fontSize: "0.9rem", color: "#bbb" }}>
              Welcome back, {me.name || "Anon"}!
            </p>
          ) : (
            <p style={{ fontSize: "0.9rem", color: "#bbb" }}>
              (Not Verified) Please log in before spinning or saving
              preferences.
            </p>
          )}
        </div>
      )}

      <MysteryWheel />
      <JoinedUsersTable />
      <ActivityPreferences />
    </div>
  )
}

/** The single CTA (bottom-right) that triggers join, login, share, spin, or respin. */
function FloatingCTA() {
  const [users, setUsers] = useRecoilState(usersAtom)
  const [current, setCurrent] = useRecoilState(currentUserAtom)
  const joined = useRecoilValue(joinedUsersSelector)
  const [spinState, setSpinState] = useRecoilState(spinAtom)
  const showToast = useToast()

  // local states for share modal & phone modal
  const [showShare, setShowShare] = useState(false)
  const [showPhone, setShowPhone] = useState(false)

  // Decide CTA text
  let cta = "Select user first"
  let handleClick = () => showToast("Pick user above first!")

  if (current) {
    const me = users[current]
    if (!me.joined) {
      cta = "Join session"
      handleClick = () => {
        setUsers({
          ...users,
          [current]: { ...me, joined: true },
        })
        showToast("You joined!")
      }
    } else if (!me.verified) {
      cta = "Log in"
      handleClick = () => setShowPhone(true)
    } else {
      // user is verified
      const isSpun = spinState.activity !== null
      if (joined.length < 2) {
        cta = "Share with a friend"
        handleClick = () => setShowShare(true)
      } else if (!isSpun) {
        cta = "Spin the wheel!"
        handleClick = () => {
          showToast("Tap the wheel itself to spin!")
        }
      } else {
        cta = "Respin for a new activity"
        handleClick = () => {
          // reset spin
          setUsers((prev) => {
            const newObj = { ...prev }
            Object.keys(newObj).forEach((k) => {
              const slot = k as UserSlot
              newObj[slot] = { ...newObj[slot], isLucky: false }
            })
            return newObj
          })
          // remove the spinState
          // We'll do that in the next line
          setSpinState({ activity: null, time: null, place: null })
          showToast("Respin the wheel now!")
        }
      }
    }
  }

  return (
    <>
      {showShare && <ShareSheet onClose={() => setShowShare(false)} />}
      {showPhone && (
        <PhoneModal
          onClose={() => setShowPhone(false)}
          onVerify={(phone, name) => {
            if (!current) return
            const me = users[current]
            setUsers({
              ...users,
              [current]: {
                ...me,
                verified: true,
                phone,
                name: name || me.name || "Anon",
              },
            })
            setShowPhone(false)
          }}
        />
      )}
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        style={{
          position: "fixed",
          bottom: 20,
          right: 20,
          background: "#7F5AF0",
          color: "#fff",
          borderRadius: 30,
          padding: "0.8rem 1.5rem",
          cursor: "pointer",
          fontWeight: 600,
          boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
          fontSize: "0.95rem",
          zIndex: 999,
        }}
        onClick={handleClick}
      >
        {cta}
      </motion.div>
    </>
  )
}

/** Final export with RecoilRoot + main UI + CTA + toast container. */
export default function FullPartialRevealDemo() {
  return (
    // <RecoilRoot>
    <div
      style={{
        minHeight: "100vh",
        background: "#000",
        color: "#fff",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <ToastContainer />
      <MainUI />
      <FloatingCTA />
    </div>
    // </RecoilRoot>
  )
}
